import { IconBadge } from '@carvana/ds-badge';
import Card from '@carvana/ds-card';
import { TagCvna } from '@carvana/ds-icons';
import { icon } from '@carvana/ds-styles';
import React from 'react';
import { EventedModal, ModalNames } from '|/app/modals';
import SlateWsysImage from '|/app/purchase/cart/steps/trade-in/assets/slate-wsys.png';
import { useBrand, useBrandTokens } from '|/brand';
import WsysImage from '|/icons/wsys.png';
import { useWsysModalVm, type CardData, type SectionData } from './use-wsys-modal-vm';
import styles from './wsys-modal.module.css';

interface WsysModalProps {
  isOpen: boolean;
  onClose: () => void;
  tradeIncentive: number;
  taxSavings: number;
  vehicleMake: string;
  tradeValue: string;
  stateName: string;
  /**
   * Only needed when this opens from another modal. ds-modal derives its own z-index from
   * nesting depth, which undercounts when the opener is a sibling rather than an ancestor.
   */
  zIndex?: number;
}

const WsysModal: React.FC<WsysModalProps> = ({
  isOpen,
  onClose,
  tradeIncentive,
  taxSavings,
  stateName,
  zIndex
}) => {
  const { brand } = useBrand();
  const tokens = useBrandTokens();
  const { heroTitle, cards } = useWsysModalVm({
    tradeIncentive,
    taxSavings,
    stateName
  });

  return (
    <EventedModal
      isOpen={isOpen}
      analyticsId={ModalNames.TradeInWsys}
      onClose={onClose}
      divLine={false}
      desktopWidth="480px"
      contentPadding={false}
      mobileStyle="inset"
      zIndex={zIndex}
      classes={{
        header: styles.modalHeader,
        body: styles.modalBody
      }}
      aria-labelledby="wsys-modal-title"
    >
      <div className={styles.heroWrapper}>
        <svg
          className={styles.heroBackground}
          xmlns="http://www.w3.org/2000/svg"
          width="480"
          height="128"
          viewBox="0 0 480 128"
          fill="none"
          preserveAspectRatio="none"
        >
          <path
            d="M480 0V126.508C458.241 127.623 436.146 127.221 413.772 127.632C256.378 130.526 111.854 116.238 0 88.2771V0H480Z"
            fill={tokens.heroAccentFill}
          />
        </svg>
        <div className={styles.heroContent}>
          <div id="wsys-modal-title" className={styles.heroHeading}>
            {heroTitle}
          </div>
        </div>
        <img
          src={brand === 'slate' ? SlateWsysImage : WsysImage}
          alt="Trade in your vehicle"
          className={styles.heroImage}
        />
      </div>
      <div className={styles.content}>
        {cards.map((card: CardData) => (
          <Card key={card.type} className={styles.card} showBorder margin="sm">
            <div className={styles.cardContent}>
              <IconBadge
                className={styles.badge}
                CustomIcon={() => <TagCvna color={icon.primary} />}
                priority="custom"
              >
                Bundle &amp; Save
              </IconBadge>

              {/* Render sections */}
              {card.sections.map((section: SectionData, index: number) => (
                <div key={section.label} className={styles.section}>
                  <div className={styles.cardTextBundleSave}>
                    <div className={styles.cardTitle}>{section.title}</div>
                    <div className={styles.cardDescription}>{section.description}</div>
                  </div>
                  <div className={styles.divider} />
                  <div className={styles.receiptCell}>
                    <div className={styles.receiptLabel}>{section.label}</div>
                    <div className={styles.receiptAmount}>{section.amount}</div>
                  </div>
                  {/* Add divider between sections for combined card */}
                  {card.type === 'combined' && index < card.sections.length - 1 && (
                    <div className={styles.sectionDivider} />
                  )}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </EventedModal>
  );
};

export default WsysModal;
